/**
 * LocalStorage-backed mock store for the Facilities endpoint.
 *
 * Why localStorage (vs an in-memory map like `mocks/store.ts`):
 *   - The user-facing scope of this mock is *the same submission
 *     across page reloads* — Schedule of Location persists, the user
 *     refreshes, the data is still there. An in-memory map resets on
 *     every reload, which defeats the point.
 *   - Future-proofs the dev workflow: the FE looks/feels like a real
 *     backend round-trip without forcing the dev to log in.
 *
 * Key scheme: `orion.facilities.<submissionId>`. Empty submissionId
 * (i.e. "/submissions/new" before the first save) gets bucketed under
 * `orion.facilities.draft` so the user can still play with the table.
 *
 * Concurrency: localStorage is synchronous and single-threaded per
 * tab; cross-tab edits surface via the `storage` event but we don't
 * subscribe yet (low value for the current mock).
 */
import type { Facility, FacilityWritePayload } from "../types/facility";

const SCOPE_PREFIX = "orion.facilities.";
const DRAFT_SCOPE = "draft";

function scopeKey(submissionId: string): string {
    const scope = submissionId.trim() || DRAFT_SCOPE;
    return `${SCOPE_PREFIX}${scope}`;
}

/** Safe-read: handles SSR (no window), unparseable JSON, and missing keys. */
function read(submissionId: string): Facility[] {
    if (typeof window === "undefined") return [];
    try {
        const raw = window.localStorage.getItem(scopeKey(submissionId));
        if (!raw) return [];
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? (parsed as Facility[]) : [];
    } catch {
        // Corrupt JSON shouldn't crash the UI — wipe + start clean.
        return [];
    }
}

function write(submissionId: string, facilities: Facility[]): void {
    if (typeof window === "undefined") return;
    window.localStorage.setItem(scopeKey(submissionId), JSON.stringify(facilities));
}

/** Tiny UUID-ish — enough collision resistance for a mock store
 *  scoped to one user's localStorage. crypto.randomUUID() would be
 *  cleaner but jsdom/older Safari setups can lack it. */
function newId(): string {
    return `fac-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function nowIso(): string {
    return new Date().toISOString();
}

// ─────────── public store API ─────────────────────────────

export function listFacilities(submissionId: string): Facility[] {
    return read(submissionId);
}

export function createFacility(submissionId: string, payload: FacilityWritePayload): Facility {
    const current = read(submissionId);
    const now = nowIso();
    const facility: Facility = {
        id: newId(),
        submissionId: submissionId || DRAFT_SCOPE,
        catalogFacilityId: "",
        name: "",
        providerNumber: "",
        acquired: "",
        operator: "",
        address: "",
        city: "",
        state: "",
        zip: "",
        county: "",
        geo: "",
        actuarialRate: "",
        plRetroactiveDate: "",
        glRetroactiveDate: "",
        retention: "",
        sirCredit: "",
        plLimit: "",
        glLimit: "",
        censusSkilled: "",
        censusMemoryCare: "",
        censusAssisted: "",
        censusIndependent: "",
        censusAddlExposure: "",
        maturityCredit: "",
        licensedSnf: "",
        licensedMc: "",
        licensedAlf: "",
        licensedIlf: "",
        isExcluded: false,
        duplicateSubmissionId: "",
        createdAt: now,
        updatedAt: now,
        ...payload,
    };
    write(submissionId, [...current, facility]);
    return facility;
}

export function patchFacility(
    submissionId: string,
    facilityId: string,
    payload: FacilityWritePayload,
): Facility | null {
    const current = read(submissionId);
    const idx = current.findIndex((f) => f.id === facilityId);
    if (idx === -1) return null;
    const merged: Facility = {
        ...current[idx],
        ...payload,
        updatedAt: nowIso(),
    };
    const next = [...current];
    next[idx] = merged;
    write(submissionId, next);
    return merged;
}

export function deleteFacility(submissionId: string, facilityId: string): boolean {
    const current = read(submissionId);
    const next = current.filter((f) => f.id !== facilityId);
    if (next.length === current.length) return false;
    write(submissionId, next);
    return true;
}
